import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { map, Observable } from 'rxjs';
import { UserService } from './user.service';

@Injectable({
	providedIn: 'root',
})
export class TerminalTemplateAdministrationService {
	constructor(
		private http: HttpClient,
		private userService: UserService,
	) {}

	private AVAILABLE_GROUPS = {
		time: {
			source: ['time'],
			title: [''],
			dataType: ['String'],
			format: [''],
			sort: ['None'],
		},
		name: {
			source: ['name'],
			title: [''],
			dataType: ['String'],
			format: [''],
			sort: ['None'],
		},
		items: {
			source: ['items'],
			title: [''],
			dataType: ['Number'],
			format: [''],
			sort: ['None'],
		},
	};

	getAvailableGroups() {
		return this.AVAILABLE_GROUPS;
	}

	getUrl(terminalTemplateId?: string | null) {
		return `${environment.awsEnvUri}/mes/terminaltemplates${terminalTemplateId ? `/${terminalTemplateId}` : ''}`;
	}

	getTerminalTemplates(): Observable<ITerminalTemplates> {
		return this.http.get<ITerminalTemplates>(this.getUrl(), {
			headers: this.userService.getHeaders(),
		});
	}

	getTerminalTemplateById(terminalTemplateId: string): Observable<ITerminalTemplate> {
		return this.http
			.get<{ status: number; result: ITerminalTemplate }>(this.getUrl(terminalTemplateId), {
				headers: this.userService.getHeaders(),
			})
			.pipe(map((response) => response.result));
	}

	createTerminalTemplate(terminalTemplate: IPostTerminalTemplateRequest): Observable<ITerminalTemplate> {
		const html = terminalTemplate.data.detailView;
		terminalTemplate.data.detailView = this.base64EncodeUnicode(html);

		return this.http.post<ITerminalTemplate>(
			this.getUrl(),
			{
				alias: terminalTemplate.alias,
				name: terminalTemplate.name,
				data: terminalTemplate.data,
			} as IPostTerminalTemplateRequest,
			{
				headers: this.userService.getHeaders(),
			},
		);
	}

	updateTerminalTemplate(terminalTemplate: IPutTerminalTemplateRequest): Observable<ITerminalTemplate> {
		const html = terminalTemplate.data.detailView;
		terminalTemplate.data.detailView = this.base64EncodeUnicode(html);

		return this.http.put<ITerminalTemplate>(
			this.getUrl(terminalTemplate.id),
			{
				alias: terminalTemplate.alias,
				name: terminalTemplate.name,
				data: terminalTemplate.data,
			} as IPutTerminalTemplateRequest,
			{
				headers: this.userService.getHeaders(),
			},
		);
	}

	base64EncodeUnicode(str: string): string {
		const utf8Bytes = new TextEncoder().encode(str);
		let binary = '';
		utf8Bytes.forEach((byte) => {
			binary += String.fromCharCode(byte);
		});
		return btoa(binary);
	}

	deleteTerminalTemplateById(terminalTemplateId: string): Observable<any> {
		return this.http.delete(this.getUrl(terminalTemplateId), {
			headers: this.userService.getHeaders(),
		});
	}
}

export interface ITerminalTemplates {
	result: ITerminalTemplate[];
}
export interface IPutTerminalTemplateRequest extends IInternalTerminalTemplate {
	id: string;
}
export interface IPostTerminalTemplateRequest extends IInternalTerminalTemplate {}
export interface ITerminalTemplate extends IInternalTerminalTemplate {
	id: string | null;
}

interface IInternalTerminalTemplate {
	alias: string;
	name?: string;
	data: ITerminalTemplateData;
}

export interface ITerminalTemplateData {
	groupList?: IItemOperationSetup[] | null;
	unitList?: IItemOperationSetup[] | null;
	detailView?: string | null;
	sqlQuery?: string | null;
}

export interface IItemOperationSetup {
	source: string;
	title: string;
	dataType: IDataType;
	format: string;
	sort: ISortSetting;
}

export enum ISortSetting {
	None = 'None',
	Ascending = 'Ascending',
	Descending = 'Descending',
}

export enum IDataType {
	String = 'String',
	Number = 'Number',
	Time = 'Time',
	Date = 'Date',
}
